import { put, select, takeEvery } from 'redux-saga/effects';

import { PassportGroupActionsEnum } from '../enums/PassportGroupActionsEnum';
import { sagaWrapper } from '../../../CorePackage/MainModule/common/sagaWrapper';
import { IPassportGroup } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroup';
import { passportGroupSelector } from '../selectors/passportGroupSelector';
import { IPassportGroupForm } from '../../PassportGroupFormModule/interfaces/IPassportGroupForm';
import { IGroupConditionForm } from '../../PassportGroupFormModule/interfaces/IGroupConditionForm';
import { passportGroupSetInitFormAction } from '../actions/passportGroupActions';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { IPassportGroupConditionWithValues } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupConditionWithValues';
import { createTerritoryUnitsSelectOption } from '../../../CorePackage/CatalogsModule/common/createTerritoryUnitsSelectOption';
import { createDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryKnoOrganizationSelectOption';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { createKnoAdminLevelSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnoAdminLevelSelectOption';
import { createDictionarySupervisionTypeExtendedSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionarySupervisionTypeSelectOption';
import { createKnmTypeSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmTypeSelectOption';
import { createDictionaryKnmKindSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryKnmKindSelectOption';
import { createDictionaryErvkSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryErvkSelectOption';
import { createDictionaryRiskIndicatorySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryRiskIndicatorySelectOption';
import { unableToRefuseSelectOptions } from '../../PassportGroupFormModule/constants/unableToRefuseSelectOptions';
import { publishedStatusesSelectOptions } from '../../../NewsPackage/MainModule/constants/publishedStatuses';
import { PassportGroupPassportTypeEnum } from '../../PassportGroupFormModule/enums/PassportGroupPassportTypeEnum';
import { createKnmErpStatusesOption } from '../../../CorePackage/CatalogsModule/common/createKnmErpStatusesOption';
import { createKnmStatusesOption } from '../../../CorePackage/CatalogsModule/common/createKnmStatusesOption';
import { createDictionaryProfKindSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryProfKindSelectOption';
import { createDictionaryDecisionKindSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryDecisionKindSelectOption';
import { createDictionaryTargetEntitySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryTargetEntitySelectOption';
import { createRegisterTypesSelectOption } from '../../../CorePackage/CatalogsModule/common/createRegisterTypesSelectOption';
import { createMspCategorySelectOption } from '../../../CorePackage/CatalogsModule/common/createMspCategorySelectOption';
import { createDictionarySubjectTypeSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionarySubjectTypeSelectOption';
import { createDictionarySubjectKindSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionarySubjectKindSelectOption';
import { createDictionaryRiskCategorySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryRiskCategorySelectOption';
import { createDictionaryEventTypeSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryEventTypeSelectOption';
import { structuralUnitConverter } from '../../../APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import { createDictionaryPermitDocsSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryPermitDocsSelectOption';

const groupConditionFormFromRaw = (data: IPassportGroupConditionWithValues): IGroupConditionForm => ({
  name: data.name || '',
  category: data.category || undefined,
  commonConditions: {
    startDate: data.commonConditions?.startDate
      ? formatDate({ date: data.commonConditions.startDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
    endDate: data.commonConditions?.endDate
      ? formatDate({ date: data.commonConditions.endDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
    domains: data.commonConditions?.domains?.map(createTerritoryUnitsSelectOption) || undefined,
    includeDomainChild: data.commonConditions?.includeDomainChild || false,
    knoOrganizations:
      data.commonConditions?.knoOrganizations?.map(item =>
        createDictionaryKnoOrganizationSelectOption(DictionaryUUIDsEnum.KNO_ORGANIZATIONS, item),
      ) || undefined,
    includeKnoOrganizationChild: data.commonConditions?.includeKnoOrganizationChild || false,
    knoOrganizationLevels:
      data.commonConditions?.knoOrganizationAdminLevels?.map(createKnoAdminLevelSelectOption) || undefined,
    kindControls: data.commonConditions?.kindControls?.map(
      item =>
        createDictionarySupervisionTypeExtendedSelectOption(DictionaryUUIDsEnum.SUPERVISION_TYPE, item, false) ||
        undefined,
    ),
    knmTypes: data.commonConditions?.knmTypes?.map(createKnmTypeSelectOption) || undefined,
    plans:
      data.commonConditions?.plans?.map(item => ({
        id: item,
        value: item.toString(),
        label: item.toString(),
      })) || undefined,
    knmKinds:
      data.commonConditions?.knmKinds?.map(item =>
        createDictionaryKnmKindSelectOption(DictionaryUUIDsEnum.KNM_KIND, item),
      ) || undefined,
    pmKinds:
      data.commonConditions?.pmKinds?.map(item =>
        createDictionaryProfKindSelectOption(DictionaryUUIDsEnum.PROF_KIND, item),
      ) || undefined,
    decisionKinds:
      data.commonConditions?.decisionKinds?.map(item =>
        createDictionaryDecisionKindSelectOption(DictionaryUUIDsEnum.DECISION_KIND, item),
      ) || undefined,
    reasons:
      data.commonConditions?.reasons?.map(item => createDictionaryErvkSelectOption(DictionaryUUIDsEnum.ERVK, item)) ||
      undefined,
    riskIndikators:
      data.commonConditions?.riskIndikators?.map(item => {
        const selectOption = createDictionaryRiskIndicatorySelectOption(DictionaryUUIDsEnum.RISK_INDICATORY, item);

        return {
          ...selectOption,
          label: selectOption.shortTitle || selectOption.title,
        };
      }) || undefined,
    erpIds:
      data.commonConditions?.erpIds?.map(item => ({
        id: item,
        value: item,
        label: item,
      })) || undefined,
    unableToRefuse:
      typeof data.commonConditions?.isUnableToRefuse === 'boolean'
        ? unableToRefuseSelectOptions.find(item => item.value === data.commonConditions?.isUnableToRefuse)
        : undefined,
  },
  assignmentConditions: {
    assigmentStartDate: data.assignmentConditions?.assigmentStartDate
      ? formatDate({ date: data.assignmentConditions.assigmentStartDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
    assigmentEndDate: data.assignmentConditions?.assigmentEndDate
      ? formatDate({ date: data.assignmentConditions.assigmentEndDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
    assignmentNumbers:
      data.assignmentConditions?.assignmentNumbers?.map(item => ({
        id: item,
        label: item,
        value: item,
      })) || undefined,
  },
  subjectConditions: {
    subjectTypes:
      data.subjectConditions?.subjectTypes?.map(item =>
        createDictionaryTargetEntitySelectOption(DictionaryUUIDsEnum.TARGET_ENTITY, item),
      ) || undefined,
    regTypes: data.subjectConditions?.regTypes?.map(createRegisterTypesSelectOption) || undefined,
    mspCategories: data.subjectConditions?.mspCategories?.map(createMspCategorySelectOption) || undefined,
    checkResultStartDate: data.subjectConditions?.checkResultStartDate
      ? formatDate({ date: data.subjectConditions.checkResultStartDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
    checkResultEndDate: data.subjectConditions?.checkResultEndDate
      ? formatDate({ date: data.subjectConditions.checkResultEndDate, dateFormat: DATE_FORMAT_LONG_FNS })
      : undefined,
  },
  objectConditions: {
    ervkNumbers:
      data.objectConditions?.ervkNumbers?.map(item => ({
        id: item.ervkNumber,
        value: item.ervkNumber,
        label: `${item.ervkNumber} - ${item.name}`,
      })) || undefined,
    objectTypes:
      data.objectConditions?.objectTypes?.map(item =>
        createDictionarySubjectTypeSelectOption(DictionaryUUIDsEnum.SUBJECT_TYPE, item),
      ) || undefined,
    objectKinds:
      data.objectConditions?.objectKinds?.map(item =>
        createDictionarySubjectKindSelectOption(DictionaryUUIDsEnum.SUBJECT_KIND, item),
      ) || undefined,
    objectSubKinds:
      data.objectConditions?.objectSubKinds?.map(item =>
        createDictionarySubjectKindSelectOption(DictionaryUUIDsEnum.SUBJECT_SUB_KIND, item),
      ) || undefined,
    riskCategories:
      data.objectConditions?.riskCategories?.map(item =>
        createDictionaryRiskCategorySelectOption(DictionaryUUIDsEnum.RISK_CATEGORY, item),
      ) || undefined,
    dangerClasses:
      data.objectConditions?.dangerClasses?.map(item =>
        createDictionaryRiskCategorySelectOption(DictionaryUUIDsEnum.RISK_CATEGORY, item),
      ) || undefined,
  },
  eventSubjectConditions: {
    requirements:
      data.eventSubjectConditions?.requirements?.map(item => ({
        ...item,
        structuralUnits: item.structuralUnits?.map(suItem => ({
          ...suItem,
          structuralUnit: structuralUnitConverter(suItem.structuralUnit),
        })),
      })) || undefined,
    docTargets: data.eventSubjectConditions?.docTargets?.map(
      item =>
        createDictionaryPermitDocsSelectOption(DictionaryUUIDsEnum.PERMIT_DOCS, {
          idValue: item.targetId,
          value: item,
        }) || undefined,
    ),
  },
  eventConditions: {
    eventTypes:
      data.eventConditions?.eventTypes?.map(item =>
        createDictionaryEventTypeSelectOption(DictionaryUUIDsEnum.EVENT_TYPE, item),
      ) || undefined,
  },
  statusConditions: {
    statuses:
      data.statusConditions?.statuses?.map(item =>
        data.category === PassportGroupPassportTypeEnum.ERP
          ? createKnmErpStatusesOption(item, [])
          : createKnmStatusesOption(item),
      ) || undefined,
    publishedStatus: data.statusConditions?.publishedStatus
      ? publishedStatusesSelectOptions.find(item => item.value === data.statusConditions?.publishedStatus)
      : undefined,
  },
});

export function* watchPassportGroupInitFormSaga() {
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_INIT_DETAIL_FORM, passportGroupInitDetailFormSaga);
}

function* passportGroupInitDetailFormSaga() {
  function* funcTry() {
    const passportGroup: IPassportGroup | undefined = yield select(passportGroupSelector);
    const passportGroupInitForm: IPassportGroupForm = {
      prosecutorOrganization: passportGroup?.prosecutorOrganization
        ? {
            id: passportGroup.prosecutorOrganization.id || '',
            label: passportGroup.prosecutorOrganization.name || '',
            value: passportGroup.prosecutorOrganization.id,
          }
        : undefined,
      includes: passportGroup?.filter.include ? [groupConditionFormFromRaw(passportGroup.filter.include)] : undefined,
      exceptions: passportGroup?.filter.exclude ? [groupConditionFormFromRaw(passportGroup.filter.exclude)] : undefined,
      reason: passportGroup?.reason?.id
        ? {
            id: passportGroup.reason.id,
            label: passportGroup.reason.name,
            value: passportGroup.reason.id,
          }
        : undefined,
      reasonFile: passportGroup?.reasonFile || undefined,
    };

    yield put(passportGroupSetInitFormAction(passportGroupInitForm));
  }

  yield sagaWrapper({ funcTry });
}
